/**
 * Which region the app is showing — cities plan C2.2 (D2).
 *
 *     current = override ?? stayRegion(today) ?? profile.home_region
 *
 * - **override** — a choice made on this device, in Account → Region. It is
 *   `userKey('sl-region')` in localStorage: per device and per user, never a
 *   `profiles` column (two devices can look at two regions) and never in the
 *   query cache (it must survive `CACHE_BUSTER`). `null` is *Follow my stays*.
 * - **stayRegion(today)** — the `region_id` of a `travel` or `home2` stay that
 *   covers today. A stay with no `region_id` (Rome, Vilnius: in no region the
 *   app shows) moves nothing; C0.2 wrote that down, and "null is not home"
 *   is the reading that keeps a weekend in Rome from switching the app to
 *   Berlin's feed on a Boston stay.
 * - **home** — `profiles.home_region`, `'berlin'` until the row says
 *   otherwise.
 *
 * ## Changing it re-renders the whole app, on purpose
 *
 * `regions.ts` holds the region ambiently (`currentRegion(data)` is called
 * from twenty places that cannot all take a hook) and `zone.ts` answers
 * `hereZone()` from it. Setting an ambient value renders nothing — the
 * warning C2.1's worker left — so a component that read Berlin's zone keeps
 * its Berlin day until something re-renders it, and the Feed would show
 * Boston rows under a Berlin "today". `RegionRoot` is the something: it
 * publishes the resolved region during its own render, before any child
 * reads it, and **keys its children on the region id**, so a switch
 * remounts the tree under the new value. The queries survive (they live in
 * the client, not the tree), the hash route survives, a sheet that was open
 * does not — which is what *the sheet closes* in the brief asks for.
 *
 * ## The reader rule for a region's place
 *
 * `placeFor` is C0.2's rule, written once: the person's `profile_places` row
 * for the region; else, when the region is `profiles.home_region`, the
 * `profiles` columns; else *no place*, not Neukölln's pin carried over to
 * Concord.
 */
import { createElement, Fragment, useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react'
import { useProfile } from './profile'
import { useProfileRow, useStays, useProfilePlaces } from './queries'
import {
  BUNDLED, HOME_REGION_ID, getActiveRegionId, homeRegionOf, setActiveRegionId, useRegionsNow, zonesOf,
  type District, type Region, type RegionsData,
} from './regions.ts'
import type { Profile } from './profileDefaults.ts'
import type { Stay } from './types'
import { userKey } from './userStorage.ts'
import { dayIn, setZones } from './zone.ts'
import { currenciesOf, setCurrencies } from './currency.ts'
import { setHereCountry } from './releaseOn.ts'

/** One `Currencies` per regions object, so its town memo survives the root
 *  re-rendering (`zonesOf` does the same). */
const currencyCache = new WeakMap<RegionsData, ReturnType<typeof currenciesOf>>()
function currenciesFor(data: RegionsData) {
  let c = currencyCache.get(data)
  if (!c) { c = currenciesOf(data, getActiveRegionId); currencyCache.set(data, c) }
  return c
}

// Until `RegionRoot` has rendered, prices read the bundled regions' currencies
// (a row's town's region's), as `regions.ts` does for zones.
setCurrencies(currenciesFor(BUNDLED))

/** The device preference's base key. `userKey` adds `:<uid>`. Not `sb-*` and
 *  not `sl-theme`, so a sign-out sweeps it with the rest of that person's
 *  keys (`clearUserStorage`) and nothing sweeps anyone else's. */
export const REGION_KEY = 'sl-region'

/** A stay as C0.2 left it: `region_id` is younger than a persisted cache of
 *  `stays`, so it may be absent, not only null. */
export type RegionStay = Stay & { region_id?: string | null }

export type RegionSource = 'override' | 'stay' | 'home'

export interface CurrentRegion {
  region: Region
  source: RegionSource
  /** The stay covering today that names a region, whichever region is
   *  current — the Region section tags it "staying", and Today's header
   *  reads its end date when it is the current region's. Null when none. */
  stay: RegionStay | null
}

// ---------------------------------------------------------------------------
// The stored choice
// ---------------------------------------------------------------------------

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

function browserStore(): Store | undefined {
  try { return globalThis.localStorage } catch { return undefined }
}

/** This person's key on this device. */
export const storedRegionKey = (): string => userKey(REGION_KEY)

function readRaw(key: string, storage: Store | undefined): string | null {
  try { return storage?.getItem(key) || null } catch { return null }
}

/**
 * The stored choice, or `null` for *Follow my stays*. A key naming a region
 * that is not in `known` — one a later migration removed, or a hand-edit — is
 * no choice at all, and so resolves to the stay or to home: a stale id must
 * never leave the app on a region with no rows, no zone and no pin.
 */
export function readStoredRegion(
  known: ReadonlySet<string> | readonly string[],
  storage: Store | undefined = browserStore(),
  key: string = storedRegionKey(),
): string | null {
  const id = readRaw(key, storage)
  if (!id) return null
  return (Array.isArray(known) ? known.includes(id) : (known as ReadonlySet<string>).has(id)) ? id : null
}

// One module-level mirror of the stored choice, so every `useCurrentRegion()`
// in the tree agrees on the same render, and so `useSyncExternalStore` has a
// snapshot that does not re-read localStorage per call.
const cache = new Map<string, string | null>()
const listeners = new Set<() => void>()
const notify = () => { for (const l of listeners) l() }

function overrideAt(key: string): string | null {
  if (!cache.has(key)) cache.set(key, readRaw(key, browserStore()))
  return cache.get(key) ?? null
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  // Another tab on the same device changed it.
  const onStorage = (e: StorageEvent) => {
    if (e.key && e.key.startsWith(`${REGION_KEY}:`)) { cache.delete(e.key); listener() }
  }
  if (typeof window !== 'undefined') window.addEventListener('storage', onStorage)
  return () => {
    listeners.delete(listener)
    if (typeof window !== 'undefined') window.removeEventListener('storage', onStorage)
  }
}

/**
 * Show `id` on this device from now on, or `null` to follow the stays again.
 * Writes and removes **its own key and nothing else** (rule 7: the app's
 * localStorage holds the signed-in session). The caller does not need to
 * force a re-render: every `useCurrentRegion()` is subscribed, and
 * `RegionRoot` remounts the tree on the id changing.
 */
export function setRegionOverride(id: string | null, storage: Store | undefined = browserStore()): void {
  const key = storedRegionKey()
  try {
    if (id) storage?.setItem(key, id)
    else storage?.removeItem(key)
  } catch { /* storage blocked: the choice lasts until the tab closes */ }
  cache.set(key, id || null)
  notify()
}

// ---------------------------------------------------------------------------
// The rule
// ---------------------------------------------------------------------------

/**
 * The stay that covers `now`, among those that name a region the app knows.
 *
 * "Today" is the day *in the stay's own region's zone*, not in the home
 * zone: a Boston stay that ends on 8 December is still on at 18:00 Boston on
 * the 8th, which is already the 9th in Berlin. Reading it in Berlin's zone
 * would send the app home six hours before the plane. Inclusive of both
 * ends, so a stay ending today counts. Several: the one that started last
 * (a short trip inside a long stay is where he is), then the later end.
 */
export function stayCovering(stays: readonly RegionStay[], regions: RegionsData, now: Date): RegionStay | null {
  let best: RegionStay | null = null
  for (const s of stays) {
    if (s.kind !== 'travel' && s.kind !== 'home2') continue
    const region = s.region_id ? regions.regions.get(s.region_id) : undefined
    if (!region) continue
    const day = dayIn(now, region.timezone)
    if (s.starts_on > day || day > s.ends_on) continue
    if (!best || s.starts_on > best.starts_on || (s.starts_on === best.starts_on && s.ends_on > best.ends_on)) best = s
  }
  return best
}

export interface ResolveInput {
  /** The stored choice, unvalidated (or already validated: both work). */
  override: string | null
  stays: readonly RegionStay[]
  /** `profiles.home_region`, via `homeRegionOf`. */
  homeId: string
  regions: RegionsData
  now: Date
}

/** override > stay > home. Total: a region id nobody knows falls through, and
 *  the home region falls back to Berlin if even it is unknown. */
export function resolveCurrentRegion({ override, stays, homeId, regions, now }: ResolveInput): CurrentRegion {
  const stay = stayCovering(stays, regions, now)
  const byId = (id: string | null | undefined) => (id ? regions.regions.get(id) : undefined)
  // The bundled Berlin last: the live `regions` table answering with no rows
  // (or without the home region) must not be a blank app.
  const home = byId(homeId) ?? byId(HOME_REGION_ID) ?? BUNDLED.regions.get(HOME_REGION_ID)!
  const chosen = byId(override)
  if (chosen) return { region: chosen, source: 'override', stay }
  const staying = byId(stay?.region_id)
  if (staying) return { region: staying, source: 'stay', stay }
  return { region: home, source: 'home', stay }
}

// ---------------------------------------------------------------------------
// The hook
// ---------------------------------------------------------------------------

/**
 * The region being looked at, and why. Cheap to call from anywhere under the
 * providers: every query it reads is shared, and the answer is memoised on its
 * inputs plus the hour (a stay that starts tomorrow is noticed by the next
 * render after midnight; `RegionRoot` also re-reads when the tab comes back).
 */
export function useCurrentRegion(): CurrentRegion {
  const regions = useRegionsNow()
  const profile = useProfile()
  const staysQ = useStays()
  const stays = staysQ.data
  // The key follows the signed-in person: `profile.user_id` is '' until their
  // row loads, then theirs, and a second account on this device is another key.
  const key = useMemo(storedRegionKey, [profile.user_id])
  const override = useSyncExternalStore(subscribe, () => overrideAt(key), () => null)
  const homeId = homeRegionOf(profile)
  const hour = Math.floor(Date.now() / 3_600_000)
  return useMemo(
    () => resolveCurrentRegion({ override, stays: stays ?? [], homeId, regions, now: new Date() }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `hour` is the clock.
    [override, stays, homeId, regions, hour],
  )
}

/** The current region's `districts` rows (D6): what the chips group by and
 *  what a venue's `district` folds into. Berlin's are its 12 Bezirke; a
 *  Boston venue's neighbourhood folds into its town. Moved here from
 *  `Places.tsx` (C2.3) when desk Browse's Where list needed the same rows. */
export function useRegionDistricts(): District[] {
  const { districts } = useRegionsNow()
  const { region } = useCurrentRegion()
  return useMemo(() => districts.filter(d => d.region_id === region.id), [districts, region.id])
}

/** The current region's holiday table key (`regions.holidays`: 'DE-BE',
 *  'US-MA'), null for a region with none. What the Year draws as holidays and
 *  counts leave against (C2.3, D4). */
export function useRegionHolidays(): string | null {
  return useCurrentRegion().region.holidays
}

/**
 * Has the answer to *where is he* been read — or failed to be? False while the
 * stays or the profile are still on their very first request with nothing
 * cached, which is the one moment `useCurrentRegion()` answers "home" for want
 * of data rather than because home is the answer. `useFeed` waits on it: a
 * person in Concord on a fresh device would otherwise start a cold pull of
 * Berlin's feed (the larger by far) for the second it takes the stays to
 * arrive, throw it away at the remount, and pay for it on a phone. With a
 * persisted cache — every launch but the first — both queries have data from
 * the first render and this is true at once.
 *
 * A failed read counts as settled (`isPending` is false on an error), so an
 * offline first start falls to home rather than waiting for a network.
 */
export function useRegionKnown(): boolean {
  const staysPending = useStays().isPending
  const profilePending = useProfileRow().isPending
  return !staysPending && !profilePending
}

/**
 * The app root's half of a switch. Publishes the resolved region to the
 * ambient holders (`regions.ts`, and through it `zone.ts`) **during render**,
 * so the first child to read `hereZone()` already reads the new one, and keys
 * its children on the region id so a change remounts them. Setting an
 * ambient value is idempotent, so a double render (StrictMode) is harmless.
 */
export function RegionRoot({ children }: { children: ReactNode }) {
  const { region } = useCurrentRegion()
  const regions = useRegionsNow()
  setActiveRegionId(region.id)
  setZones(zonesOf(regions))
  // The symbol on a price follows the region (C2.3), by the same ambient route.
  setCurrencies(currenciesFor(regions))
  setHereCountry(region.country)

  // A stay starts or ends at midnight; a tab left open over it should notice
  // when it comes back (`useBerlinNow`'s reasoning). A state bump re-renders
  // this component, which re-resolves.
  const [, bump] = useState(0)
  useEffect(() => {
    const on = () => { if (document.visibilityState === 'visible') bump(n => n + 1) }
    document.addEventListener('visibilitychange', on)
    return () => document.removeEventListener('visibilitychange', on)
  }, [])

  return createElement(Fragment, { key: region.id }, children)
}

// ---------------------------------------------------------------------------
// A region's place (D5)
// ---------------------------------------------------------------------------

/** What a person has in a region: the shape of a `profile_places` row without
 *  the keys. */
export interface RegionPlace {
  lat: number | null
  lng: number | null
  /** 'Concord' — what Today prints. Null for the home region's profile columns. */
  label: string | null
  districts: string[]
  transit_lines: string[]
  near_km: number
  /** The MBTA stop Today's trains card reads ('place-FR-0201'); null: no card.
   *  C4.2. Only a `profile_places` row carries one — the home region's profile
   *  columns do not, so Berlin never draws the card. */
  transit_stop: string | null
}

/** The columns of a `profile_places` row `placeFor` reads. A row cached from
 *  before a column existed may lack it: every read is `?? []` / `?? null`. */
export interface PlaceRow {
  region_id: string
  lat?: number | null
  lng?: number | null
  label?: string | null
  districts?: string[] | null
  transit_lines?: string[] | null
  near_km?: number | null
  transit_stop?: string | null
}

/**
 * C0.2's reader rule. This person's row for `regionId`; failing that, the
 * `profiles` columns if `regionId` is their home region; otherwise nothing —
 * a Boston with no row has no Near me and no "near you", rather than
 * Neukölln's pin carried over to Concord.
 */
export function placeFor(
  profile: Pick<Profile, 'home_region' | 'home_lat' | 'home_lng' | 'home_districts' | 'transit_lines' | 'near_km'>,
  rows: readonly PlaceRow[] | undefined,
  regionId: string,
): RegionPlace | null {
  const row = rows?.find(r => r.region_id === regionId)
  if (row) {
    return {
      lat: row.lat ?? null, lng: row.lng ?? null, label: row.label ?? null,
      districts: row.districts ?? [], transit_lines: row.transit_lines ?? [], near_km: Number(row.near_km ?? 2.5),
      transit_stop: row.transit_stop ?? null,
    }
  }
  if ((profile.home_region ?? HOME_REGION_ID) !== regionId) return null
  return {
    lat: profile.home_lat ?? null, lng: profile.home_lng ?? null, label: null,
    districts: profile.home_districts ?? [], transit_lines: profile.transit_lines ?? [], near_km: Number(profile.near_km ?? 2.5),
    transit_stop: null,
  }
}

/** The pin, when both halves are there — `homePoint`'s rule. */
export function placePoint(p: Pick<RegionPlace, 'lat' | 'lng'> | null): { lat: number; lng: number } | null {
  return p && p.lat != null && p.lng != null ? { lat: p.lat, lng: p.lng } : null
}

/** The current region's place, or null. Reads the shared queries; the
 *  profile's own columns answer for the home region before `profile_places`
 *  has loaded, so Berlin never waits for it. */
export function useRegionPlace(): RegionPlace | null {
  const { region } = useCurrentRegion()
  const profile = useProfile()
  const rows = useProfilePlaces().data
  return useMemo(() => placeFor(profile, rows, region.id), [profile, rows, region.id])
}
